import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Upload, User } from "lucide-react";
import { api, API, formatApiError } from "../../api";
import { ORG_STAFF } from "../../constants/testIds";
import { useCelebration } from "../../contexts/CelebrationContext";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../ui/dialog";
import { Checkbox } from "../ui/checkbox";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { FormField, isFieldValid } from "../common/FormField";
import { SubmitButton } from "../common/SubmitButton";
import { Textarea } from "../ui/textarea";
import { toast } from "../ui/sonner";
import { downscaleImage } from "../../lib/image";

const EMPTY_FORM = { name: "", email: "", password: "", roleName: "", branchScope: [], status: "active", photoKey: "", aadhaar: "", pan: "", notes: "" };

// Add/Edit Staff — one dialog, two modes. Create hits `POST /staff` (name+email+password+role+
// branch_scope); edit hits `PATCH /staff/{id}` (role/branch_scope/status only — the backend
// never supports changing a staff member's email or password from this form, by design, since
// that's a self-service password-reset flow, not an admin one). Photo + KYC (Aadhaar/PAN) are
// captured on both create and edit, same optional-PII pattern as the member enrollment form's
// `MemberPersonalFields` (backend field-level-encrypts the KYC block either way).
export function StaffFormDialog({ open, onOpenChange, staff, roles, branches }) {
  const isEdit = !!staff;
  const queryClient = useQueryClient();
  const { celebrate } = useCelebration();
  const [form, setForm] = useState(EMPTY_FORM);
  const [error, setError] = useState("");
  const [photoPreview, setPhotoPreview] = useState(null);

  useEffect(() => {
    if (!open) return;
    if (staff) {
      setForm({
        name: staff.name, email: staff.email, password: "", roleName: staff.role_name,
        branchScope: staff.branch_scope || [], status: staff.status,
        photoKey: staff.photo_key || "", aadhaar: staff.kyc?.aadhaar_number || "", pan: staff.kyc?.pan_number || "", notes: staff.notes || "",
      });
    } else {
      setForm(EMPTY_FORM);
    }
    setPhotoPreview(null);
    setError("");
  }, [open, staff]);

  const toggleBranch = (id) => {
    setForm((f) => ({ ...f, branchScope: f.branchScope.includes(id) ? f.branchScope.filter((b) => b !== id) : [...f.branchScope, id] }));
  };

  const uploadPhotoMutation = useMutation({
    mutationFn: (file) => {
      const body = new FormData();
      body.append("file", file);
      return api.post("/staff/photo", body);
    },
    onSuccess: (res) => setForm((f) => ({ ...f, photoKey: res.data.photo_key })),
    onError: () => toast.error("Couldn't upload that photo — try a different file."),
  });

  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoPreview(URL.createObjectURL(file));
    // Shrink before the size check, so a straight-off-the-phone photo passes instead of being
    // rejected for pixels no avatar will ever show.
    const upload = await downscaleImage(file);
    if (upload.size > 10 * 1024 * 1024) {
      toast.error("Photo must be under 10 MB.");
      setPhotoPreview(null);
      return;
    }
    uploadPhotoMutation.mutate(upload);
  };

  const buildKyc = () => ((form.aadhaar || form.pan) ? { aadhaar_number: form.aadhaar || null, pan_number: form.pan || null } : null);

  const mutation = useMutation({
    mutationFn: () => (isEdit
      ? api.patch(`/staff/${staff.id}`, {
        name: form.name, role_name: form.roleName, branch_scope: form.branchScope, status: form.status,
        photo_key: form.photoKey || null, kyc: buildKyc(), notes: form.notes || null,
      })
      : api.post("/staff", {
        name: form.name, email: form.email, password: form.password, role_name: form.roleName, branch_scope: form.branchScope,
        photo_key: form.photoKey || null, kyc: buildKyc(), notes: form.notes || null,
      })),
    onSuccess: () => {
      if (!isEdit) celebrate("staff_added", `${form.name} can now log in.`);
      queryClient.invalidateQueries({ queryKey: ["staff-list"] });
      queryClient.invalidateQueries({ queryKey: ["staff-seats"] });
      onOpenChange(false);
    },
    onError: (e) => setError(formatApiError(e).message),
  });

  const isValid = isFieldValid("text", true, form.name) && !!form.roleName
    && (isEdit || (isFieldValid("email", true, form.email) && form.password.length >= 12));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-modal sm:rounded-[14px] max-h-[85vh] overflow-y-auto" data-testid={ORG_STAFF.formDialog}>
        <DialogHeader>
          <DialogTitle>{isEdit ? `Edit ${staff.name}` : "Add staff"}</DialogTitle>
          <DialogDescription>{isEdit ? "Role/branch changes force a re-login on all their active sessions." : "They'll be able to log in immediately with this email + password."}</DialogDescription>
        </DialogHeader>

        {error && <p className="text-sm shake-on-error" style={{ color: "var(--scarlet-neg)" }} data-testid={ORG_STAFF.formError}>{error}</p>}

        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div
              className="rounded-full overflow-hidden flex items-center justify-center shrink-0"
              style={{ width: 56, height: 56, background: "var(--scarlet-page-bg)", border: "1px solid var(--scarlet-hairline)" }}
              data-testid={ORG_STAFF.photoPreview}
            >
              {photoPreview ? (
                <img src={photoPreview} alt="" className="w-full h-full object-cover" />
              ) : form.photoKey ? (
                <img src={`${API}/staff/photo?photo_key=${encodeURIComponent(form.photoKey)}`} alt="" className="w-full h-full object-cover" />
              ) : (
                <User size={22} style={{ color: "var(--scarlet-text-secondary)" }} />
              )}
            </div>
            <label className="cursor-pointer">
              <span
                className="text-sm font-medium inline-flex items-center gap-1.5 rounded-full px-3 py-1.5"
                style={{ border: "1px solid var(--scarlet-hairline)", color: "var(--scarlet-text)" }}
              >
                {uploadPhotoMutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                {uploadPhotoMutation.isPending ? "Uploading…" : "Upload photo"}
              </span>
              <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" data-testid={ORG_STAFF.photoInput} onChange={handlePhotoChange} />
            </label>
            <span className="text-xs" style={{ color: "var(--scarlet-text-secondary)" }}>Optional — JPG, PNG or WEBP</span>
          </div>
          <FormField label="Full name" required testId={ORG_STAFF.nameInput} value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
          {!isEdit && (
            <>
              <FormField label="Email" required type="email" testId={ORG_STAFF.emailInput} value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
              <FormField label="Temporary password" required type="password" testId={ORG_STAFF.passwordInput} value={form.password} onChange={(v) => setForm({ ...form, password: v })} helperText="At least 12 characters." />
            </>
          )}
          <div className="space-y-1.5">
            <Label>Role <span style={{ color: "var(--scarlet-neg)" }}>*</span></Label>
            <Select onValueChange={(v) => setForm({ ...form, roleName: v })} value={form.roleName || undefined}>
              <SelectTrigger data-testid={ORG_STAFF.roleSelect}><SelectValue placeholder="Pick a role" /></SelectTrigger>
              <SelectContent>
                {roles.map((r) => <SelectItem key={r.name} value={r.name}>{r.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Branch access</Label>
            <div className="flex flex-col gap-2 rounded-[10px] p-3" style={{ border: "1px solid var(--scarlet-hairline)" }}>
              {branches.map((b) => (
                <label key={b.id} className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox data-testid={ORG_STAFF.branchCheckbox(b.id)} checked={form.branchScope.includes(b.id)} onCheckedChange={() => toggleBranch(b.id)} />
                  {b.name}
                </label>
              ))}
              {branches.length === 0 && <p className="text-xs" style={{ color: "var(--scarlet-text-secondary)" }}>No branches yet.</p>}
            </div>
            <p className="text-xs" style={{ color: "var(--scarlet-text-secondary)" }}>Leave everything unchecked for org-wide access (e.g. Super Admin).</p>
          </div>
          <div className="rounded-[10px] p-4" style={{ border: "1px solid var(--scarlet-hairline)" }}>
            <p className="text-sm font-semibold mb-3" style={{ color: "var(--scarlet-text)" }}>
              KYC <span className="text-xs font-normal" style={{ color: "var(--scarlet-text-secondary)" }}>(optional)</span>
            </p>
            <div className="grid grid-cols-2 gap-3">
              <FormField label="Aadhaar number" testId={ORG_STAFF.kycAadhaarInput} value={form.aadhaar} onChange={(v) => setForm({ ...form, aadhaar: v })} />
              <FormField label="PAN number" testId={ORG_STAFF.kycPanInput} value={form.pan} onChange={(v) => setForm({ ...form, pan: v })} />
            </div>
          </div>
          {isEdit && (
            <div className="space-y-1.5">
              <Label>Status</Label>
              <Select onValueChange={(v) => setForm({ ...form, status: v })} value={form.status}>
                <SelectTrigger data-testid={ORG_STAFF.statusSelect}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-1.5">
            <Label>Notes <span className="text-xs font-normal" style={{ color: "var(--scarlet-text-secondary)" }}>(optional, internal only)</span></Label>
            <Textarea
              data-testid={ORG_STAFF.notesInput}
              placeholder="Anything worth remembering about this person…"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <SubmitButton
            testId={ORG_STAFF.formSubmit} disabled={!isValid} pending={mutation.isPending}
            onClick={() => { setError(""); mutation.mutate(); }}
            label={isEdit ? "Save changes" : "Add staff"}
          />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
